"use client";
import React, {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, BoxProps, CircularProgress, InputLabelProps } from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useWatch,
} from "react-hook-form";

import AppCombobox, { AppComboboxProps } from "../AppCombobox";
import { AppOptionType } from "@/models";
import AppFromLabelField from "./AppFromLabelField";

type LoadResult = {
  items: AppOptionType[];
  totalCount: number;
};

const DEFAULT_PAGE_SIZE = 20;

const mergeOptions = (
  baseOptions: AppOptionType[],
  nextOptions: AppOptionType[],
) => {
  const map = new Map<string | number, AppOptionType>();

  [...baseOptions, ...nextOptions].forEach((item) => {
    map.set(item.value, item);
  });

  return Array.from(map.values());
};

const AppFormControlLoadMoreCombobox = <T extends FieldValues>({
  label,
  control,
  name,
  rules,
  isMultiple = false,
  controlProps,
  labelProps,
  actionsButton,
  autocompleteProps,
  classNameContainer,
  helper,
  pageSize = DEFAULT_PAGE_SIZE,
  loadOptions,
  childrenWrapperProps,
}: AppFormControlLoadMoreComboboxProps<T>) => {
  const [options, setOptions] = useState<AppOptionType[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const activeKeywordRef = useRef("");
  const suppressNextFetchRef = useRef(false);
  const watchedValue = useWatch({ control, name });

  const fetchOptions = useCallback(
    async (keyword: string, nextSkip: number, shouldAppend: boolean) => {
      setIsLoading(true);
      activeKeywordRef.current = keyword;

      try {
        const result = await loadOptions({
          keyword: keyword || undefined,
          skip: nextSkip,
          take: pageSize,
        });

        if (activeKeywordRef.current !== keyword) {
          return;
        }

        setSkip(nextSkip + result.items.length);
        setHasMore(nextSkip + result.items.length < result.totalCount);
        setOptions((prev) =>
          shouldAppend ? mergeOptions(prev, result.items) : result.items,
        );
      } finally {
        if (activeKeywordRef.current === keyword) {
          setIsLoading(false);
        }
      }
    },
    [loadOptions, pageSize],
  );

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }

    const timeout = window.setTimeout(() => {
      void fetchOptions(inputValue.trim(), 0, false);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [fetchOptions, inputValue]);

  useEffect(() => {
    const isEmptyValue = isMultiple
      ? !Array.isArray(watchedValue) || watchedValue.length === 0
      : !watchedValue;

    if (!isEmptyValue) {
      return;
    }

    if (!inputValue && activeKeywordRef.current === "") {
      return;
    }

    suppressNextFetchRef.current = false;
    activeKeywordRef.current = "";
    setInputValue("");
    void fetchOptions("", 0, false);
  }, [fetchOptions, inputValue, isMultiple, watchedValue]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || !hasMore) {
      return;
    }

    void fetchOptions(inputValue.trim(), skip, true);
  }, [fetchOptions, hasMore, inputValue, isLoading, skip]);

  const listboxProps = useMemo(
    () => ({
      onScroll: (event: React.SyntheticEvent) => {
        const listboxNode = event.currentTarget;
        const reachedBottom =
          listboxNode.scrollTop + listboxNode.clientHeight >=
          listboxNode.scrollHeight - 8;

        if (reachedBottom) {
          handleLoadMore();
        }
      },
    }),
    [handleLoadMore],
  );

  return (
    <AppFromLabelField
      label={label}
      labelProps={{
        ...labelProps,
      }}
      className={classNameContainer}
      required={labelProps?.required}
      helper={helper}
      childrenWrapperProps={childrenWrapperProps}
    >
      <Box className="flex flex-row items-center">
        <Controller
          control={control}
          name={name}
          rules={rules}
          render={({ field, fieldState: { error: errorField } }) => (
            <AppCombobox
              disableCloseOnSelect={isMultiple}
              freeSolo
              multiple={isMultiple}
              options={options}
              value={
                isMultiple
                  ? Array.isArray(field.value)
                    ? field.value
                    : []
                  : field.value || null
              }
              inputValue={inputValue}
              isLoading={false}
              loading={isLoading}
              filterOptions={(x) => x}
              onOpen={() => {
                if (!options.length && !isLoading) {
                  void fetchOptions(inputValue.trim(), 0, false);
                }
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "input" || reason === "clear") {
                  if (!isMultiple && reason === "input" && field.value) {
                    field.onChange(null);
                  }
                  setInputValue(newInputValue);
                }
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setInputValue(newValue);
                  field.onChange(isMultiple ? [] : null);
                  return;
                }

                const selectedOption = newValue as AppOptionType | null;
                const selectedLabel: string =
                  !isMultiple && typeof selectedOption?.label === "string"
                    ? selectedOption.label
                    : "";

                suppressNextFetchRef.current = true;
                setInputValue(selectedLabel);
                field.onChange(newValue);
              }}
              ListboxProps={listboxProps}
              noOptionsText={isLoading ? "Đang tải..." : "Không có dữ liệu"}
              popupIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              {...autocompleteProps}
              textFieldProps={{
                error: Boolean(errorField),
                helperText: <>{errorField?.message}</>,
                ...autocompleteProps?.textFieldProps,
              }}
            />
          )}
          {...controlProps}
        />
        {actionsButton}
      </Box>
    </AppFromLabelField>
  );
};

export type AppFormControlLoadMoreComboboxProps<T extends FieldValues> = {
  label?: string | ReactNode;
  control: Control<any>;
  name: FieldPath<T>;
  isMultiple?: boolean;
  rules?: RegisterOptions;
  controlProps?: Omit<ControllerProps, "render" | "name" | "control">;
  actionsButton?: ReactNode;
  labelProps?: InputLabelProps;
  autocompleteProps?: Omit<AppComboboxProps, "options">;
  classNameContainer?: string;
  helper?: string | ReactNode;
  pageSize?: number;
  loadOptions: (params: {
    keyword?: string;
    skip: number;
    take: number;
  }) => Promise<LoadResult>;
  childrenWrapperProps?: BoxProps;
};

export default memo(AppFormControlLoadMoreCombobox);
